import { Injectable } from '@angular/core';
import { ChatSource, CsvDocumentInfo } from '../../../models/chat.model';
import { SelectedSourceState } from '../../../models/chat-ui.model';

interface RagChunkLike {
  text?: string;
  score?: number;
  metadata?: {
    id?: string;
    document_id?: string;
    original_filename?: string;
    summary?: string;
    source?: string;
    page?: number;
    [key: string]: unknown;
  };
}

@Injectable({
  providedIn: 'root'
})
export class ChatSourceMapperService {
  mapRagChunks(source: RagChunkLike[]): ChatSource[] {
    return source.map((chunk, i) => ({
      id: chunk.metadata?.original_filename || `rag-${i}`,
      original_filename: chunk.metadata?.original_filename || '',
      title: chunk.metadata?.summary || '',
      content: chunk.text || '',
      score: chunk.score || 0,
      location: chunk.metadata?.source || '',
      chunk: chunk.metadata?.page || undefined,
      metadata: chunk.metadata,
      selected: false
    }));
  }

  mapCsvDocuments(documents: CsvDocumentInfo[]): ChatSource[] {
    return documents.map((doc, i) => ({
      id: doc.source || doc.original_filename || `csv-${i}`,
      original_filename: doc.original_filename,
      title: doc.original_filename,
      content: `Archivo CSV: ${doc.original_filename}`,
      score: doc.indexed ? 1 : 0.5,
      location: doc.source,
      chunk: undefined
    }));
  }

  markAndMoveSelectedFirst(
    sources: ChatSource[],
    selectedCandidates: Array<string | null | undefined>,
    fallbackSelectedId: string | null
  ): SelectedSourceState {
    // La corona ("Fuente seleccionada") solo se gana de dos formas: que el usuario haya
    // fijado ese documento, o que se pueda CONFIRMAR que la respuesta se apoya en uno
    // solo. Coronar el `selected_chunk` con varios documentos en juego —o sin poder
    // establecer cuántos hay— presenta 1 de N como "la" fuente.
    const confirmedSingleDocument = this.canConfirmSingleDocument(sources);
    const candidates: Array<{ id: string | null | undefined; pinnedByUser: boolean }> = [
      ...selectedCandidates.map(id => ({ id, pinnedByUser: false })),
      { id: fallbackSelectedId, pinnedByUser: true }
    ];

    for (const candidate of candidates) {
      if (!candidate.id) {
        continue;
      }

      const selectedId = candidate.id;
      const selectedIndex = sources.findIndex(source => this.matchesSelectedId(source, selectedId));
      if (selectedIndex === -1) {
        continue;
      }

      const crowned = candidate.pinnedByUser || confirmedSingleDocument;
      const selectedSource = { ...sources[selectedIndex], selected: crowned };
      const reorderedSources = [
        selectedSource,
        ...sources.slice(0, selectedIndex).map(source => ({ ...source, selected: false })),
        ...sources.slice(selectedIndex + 1).map(source => ({ ...source, selected: false }))
      ];

      return {
        sources: reorderedSources,
        selectedSource,
        selectedChunkId: String(selectedId),
        hasSelection: true
      };
    }

    return {
      sources,
      selectedSource: null,
      selectedChunkId: fallbackSelectedId,
      hasSelection: false
    };
  }

  getSelectedCandidatesFromRagResult(ragResult: unknown): Array<string | null> {
    const result = ragResult as {
      selected_chunk?: {
        id?: string;
        metadata?: {
          id?: string;
          document_id?: string;
          original_filename?: string;
        };
      };
      selected_chunk_id?: string;
    };

    return [
      result.selected_chunk?.metadata?.id || null,
      result.selected_chunk?.metadata?.document_id || null,
      result.selected_chunk?.metadata?.original_filename || null,
      result.selected_chunk_id || null,
      result.selected_chunk?.id || null
    ];
  }

  findSelectedSourceInMessages(messages: Array<{ type?: string; sources?: ChatSource[] }>): SelectedSourceState {
    for (const message of messages) {
      if (message.type !== 'system' || !Array.isArray(message.sources) || message.sources.length === 0) {
        continue;
      }

      const originalSelected = message.sources.find(source => source.selected) || null;
      const sources = message.sources.map(source => ({ ...source, selected: !!source.selected }));

      if (!originalSelected) {
        // El historial no trae ninguna selección: exponemos las fuentes para poder
        // listarlas, pero no coronamos la primera solo por ser la primera del array.
        return {
          sources,
          selectedSource: null,
          selectedChunkId: null,
          hasSelection: false
        };
      }

      // Devolvemos la copia que vive en `sources` para que el panel pueda comparar por
      // identidad (`view.selectedSource === source`) al resaltar la fila activa.
      const selectedSource = sources[message.sources.indexOf(originalSelected)];
      return {
        sources,
        selectedSource,
        selectedChunkId: selectedSource.metadata?.id ? String(selectedSource.metadata.id) : null,
        hasSelection: true
      };
    }

    return {
      sources: [],
      selectedSource: null,
      selectedChunkId: null,
      hasSelection: false
    };
  }

  /**
   * ¿Podemos CONFIRMAR que la respuesta se apoya en un solo documento? Solo entonces
   * coronar una fuente dice la verdad.
   *
   * Se mide solo por `metadata.document_id`: es la única identidad documental comparable
   * entre chunks (mezclarla con `original_filename` haría que dos chunks del mismo
   * documento —uno con id y otro sin él— parecieran dos documentos).
   *
   * La carga de la prueba está del lado de coronar: si hay varias fuentes y alguna no
   * trae `document_id`, la identidad NO es establecible y no coronamos. En el retrieval
   * `document_id` siempre viene (`postgres_provider.py:87,130` y
   * `search_candidate_loader.py:250` lo setean como key incondicional), pero en un HIT DE
   * CACHE los chunks salen de la fila cacheada y `semantic_audit_lookup_workflow.py:110`
   * puede devolver `chunk_ids` en vez de chunks con metadata. Asumir "un solo documento"
   * ahí conservaría la corona falsa justo donde el usuario no puede notarla.
   */
  private canConfirmSingleDocument(sources: ChatSource[]): boolean {
    if (sources.length <= 1) {
      // Una sola fuente: "la fuente" es trivialmente cierto.
      return true;
    }
    const documentIds = new Set<string>();
    for (const source of sources) {
      const documentId = source.metadata?.document_id;
      if (documentId === undefined || documentId === null || documentId === '') {
        return false;
      }
      documentIds.add(String(documentId));
      if (documentIds.size > 1) {
        return false;
      }
    }
    return true;
  }

  private matchesSelectedId(source: ChatSource, selectedId: string): boolean {
    const candidates = [
      source.metadata?.id,
      source.metadata?.document_id,
      source.metadata?.original_filename,
      source.id,
      source.original_filename
    ];

    return candidates.some(candidate => candidate !== undefined && candidate !== null && String(candidate) === String(selectedId));
  }
}
